import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

export default function AllClients() {
  const { profile } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [selectedClient, setSelectedClient] = useState(null);
  const [editingNotes, setEditingNotes] = useState(false);
  const [coaches, setCoaches] = useState([]);
  const [reassigning, setReassigning] = useState(false);
  const [newCoachId, setNewCoachId] = useState('');
  const [reassignSuccess, setReassignSuccess] = useState('');

  useEffect(() => {
    fetchAllClients();
  }, []);

  useEffect(() => {
    fetchCoaches();
  }, []);

  const fetchCoaches = async () => {
    const { data } = await supabase
      .from('profiles')
      .select('id, full_name, email')
      .in('role', ['coach', 'senior_coach'])
      .order('full_name');
    setCoaches(data || []);
  };

  const handleReassignCoach = async () => {
    if (!newCoachId || !selectedClient) return;
    setReassigning(true);
    setReassignSuccess('');
    try {
      const { error } = await supabase
        .from('clients')
        .update({ coach_id: newCoachId })
        .eq('id', selectedClient.id);
      if (error) throw error;
      setReassignSuccess('Coach updated successfully.');
      setSelectedClient(prev => ({
        ...prev,
        coach_id: newCoachId,
        coach: coaches.find(c => c.id === newCoachId)
      }));
      fetchAllClients();
    } catch (err) {
      setReassignSuccess('Error: ' + err.message);
    } finally {
      setReassigning(false);
    }
  };

  const fetchAllClients = async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/clients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setClients(data || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const filtered = clients.filter(c => {
    const matchSearch = !search ||
      c.full_name?.toLowerCase().includes(search.toLowerCase()) ||
      c.email?.toLowerCase().includes(search.toLowerCase()) ||
      c.coach?.full_name?.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === 'all' ||
      (filter === 'active' && c.subscriptions?.some(s => s.status === 'active')) ||
      (filter === 'inactive' && !c.subscriptions?.some(s => s.status === 'active'));
    return matchSearch && matchFilter;
  });

  const activeCount = clients.filter(c => c.subscriptions?.some(s => s.status === 'active')).length;

  return (
    <div>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: 32, marginBottom: 8 }}>All Clients</h1>
        <p style={{ color: 'var(--text-secondary)' }}>
          {clients.length} client{clients.length !== 1 ? 's' : ''} across all coaches · {activeCount} with active subscriptions
        </p>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {[
          { key: 'all', label: `All (${clients.length})` },
          { key: 'active', label: `Active (${activeCount})` },
          { key: 'inactive', label: `Inactive (${clients.length - activeCount})` }
        ].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            className={filter === f.key ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}>
            {f.label}
          </button>
        ))}
        <input className="input" placeholder="Search by name, email or coach..."
          value={search} onChange={e => setSearch(e.target.value)}
          style={{ maxWidth: 260, marginLeft: 'auto' }} />
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 32, color: 'var(--text-secondary)' }}>Loading...</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text-muted)' }}>
            No clients found.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Their Coach</th>
                  <th>Active Subscriptions</th>
                  <th>Status</th>
                  <th>Since</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(c => {
                  const activeSubs = c.subscriptions?.filter(s => s.status === 'active').length || 0;
                  return (
                    <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => setSelectedClient(c)}>
                      <td>
                        <div style={{ fontWeight: 500 }}>{c.full_name}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{c.email}</div>
                      </td>
                      <td style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                        {c.coach?.full_name || '—'}
                      </td>
                      <td>
                        <span style={{
                          background: activeSubs > 0 ? 'rgba(76,175,130,0.12)' : 'rgba(90,85,80,0.2)',
                          color: activeSubs > 0 ? 'var(--success)' : 'var(--text-muted)',
                          padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500
                        }}>
                          {activeSubs} active
                        </span>
                      </td>
                      <td><span className={`badge badge-${c.status}`}>{c.status}</span></td>
                      <td style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                        {new Date(c.created_at).toLocaleDateString('en-CA')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {/* Client detail modal */}
      {selectedClient && (
        <div className="modal-overlay" onClick={() => { setSelectedClient(null); setNewCoachId(''); setReassignSuccess(''); }}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <h3 style={{ fontSize: 20 }}>{selectedClient.full_name}</h3>
              <button onClick={() => setSelectedClient(null)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 20, cursor: 'pointer' }}>✕</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <div className="label" style={{ marginBottom: 4 }}>Email</div>
                  <div style={{ fontSize: 14 }}>{selectedClient.email || '—'}</div>
                </div>
                <div>
                  <div className="label" style={{ marginBottom: 4 }}>Phone</div>
                  <div style={{ fontSize: 14 }}>{selectedClient.phone || '—'}</div>
                </div>
              </div>
              <div>
                <div className="label" style={{ marginBottom: 4 }}>Coach</div>
                <div style={{ fontSize: 14 }}>{selectedClient.coach?.full_name || '—'}</div>
              </div>
              {(selectedClient.address_line1 || selectedClient.city) && (
                <div>
                  <div className="label" style={{ marginBottom: 4 }}>Address</div>
                  <div style={{ fontSize: 14 }}>
                    {selectedClient.address_line1 && <div>{selectedClient.address_line1}</div>}
                    {selectedClient.address_line2 && <div>{selectedClient.address_line2}</div>}
                    {(selectedClient.city || selectedClient.province || selectedClient.postal_code) && (
                      <div>{[selectedClient.city, selectedClient.province, selectedClient.postal_code].filter(Boolean).join(', ')}</div>
                    )}
                    {selectedClient.country && <div>{selectedClient.country}</div>}
                  </div>
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <div className="label" style={{ marginBottom: 4 }}>Status</div>
                  <span className={`badge badge-${selectedClient.status}`}>{selectedClient.status}</span>
                </div>
                <div>
                  <div className="label" style={{ marginBottom: 4 }}>Active Subscriptions</div>
                  <div style={{ fontSize: 14 }}>{selectedClient.subscriptions?.filter(s => s.status === 'active').length || 0}</div>
                </div>
              </div>
              <div>
                <div className="label" style={{ marginBottom: 4 }}>Joined</div>
                <div style={{ fontSize: 14 }}>{new Date(selectedClient.created_at).toLocaleDateString('en-CA')}</div>
              </div>
              {selectedClient.last_payment_at && (
                <div>
                  <div className="label" style={{ marginBottom: 4 }}>Last Payment</div>
                  <div style={{ fontSize: 14 }}>{new Date(selectedClient.last_payment_at).toLocaleDateString('en-CA')}</div>
                </div>
              )}
              <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16, marginTop: 4 }}>
                <div className="label" style={{ marginBottom: 8 }}>Reassign Coach</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <select
                    className="input"
                    value={newCoachId}
                    onChange={e => setNewCoachId(e.target.value)}
                    style={{ flex: 1, fontSize: 13 }}
                  >
                    <option value="">Select new coach...</option>
                    {coaches.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.full_name} ({c.email})
                      </option>
                    ))}
                  </select>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={handleReassignCoach}
                    disabled={reassigning || !newCoachId}
                  >
                    {reassigning ? 'Saving...' : 'Reassign'}
                  </button>
                </div>
                {reassignSuccess && (
                  <div className={`alert ${reassignSuccess.startsWith('Error') ? 'alert-error' : 'alert-success'}`}
                    style={{ marginTop: 8, fontSize: 13 }}>
                    {reassignSuccess}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
